/**
 * Activity Feed Page
 *
 * Displays audit logs timeline grouped by day with icons and user information
 */

import { useEffect, useState } from 'react';
import {
  Files,
  Table as TableIcon,
  AlertTriangle,
  User,
  Eye,
  Trash2,
  Edit,
  Plus,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@loader/ui';
import { ScrollArea } from '@loader/ui';
import { Badge } from '@loader/ui';
import { Separator } from '@loader/ui';
import { Avatar, AvatarFallback } from '@loader/ui';

import { useRpc } from '@/lib/rpc';
import { useToastStore } from '@/store/useToasts';
import { FoldingCube } from '@/components/common';

/**
 * Audit log API response type
 * Matches the database schema from apps/api/src/db/schema.ts
 */
interface AuditLogApiResponse {
  id: string;
  workspace_id: string;
  user_id: string | null;
  action: 'create' | 'read' | 'update' | 'delete' | 'grant' | 'revoke';
  resource_type: 'table' | 'record' | 'user' | 'workspace' | 'linked_table';
  resource_id: string;
  details: Record<string, unknown> | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

/**
 * Get icon component for action type
 */
const getActionIcon = (action: string, resourceType: string) => {
  if (action === 'create') return Plus;
  if (action === 'delete') return Trash2;
  if (action === 'update') return Edit;
  if (action === 'read') return Eye;
  
  // Fallback to resource type
  switch (resourceType) {
    case 'version':
      return Files;
    case 'table':
    case 'linkedTable':
      return TableIcon;
    case 'user':
    case 'membership':
      return User;
    default:
      return AlertTriangle;
  }
};

/**
 * Get color classes for action type
 */
const getActionColor = (action: string) => {
  switch (action) {
    case 'create':
      return 'text-primary bg-primary/10 dark:text-primary dark:bg-primary/20';
    case 'update':
      return 'text-primary bg-primary/10 dark:text-primary dark:bg-primary/20';
    case 'delete':
      return 'text-destructive bg-destructive/10 dark:text-destructive dark:bg-destructive/20';
    case 'read':
      return 'text-primary bg-primary/10 dark:text-primary dark:bg-primary/20';
    case 'grant':
    case 'revoke':
      return 'text-yellow-600 bg-yellow-500/10 dark:text-yellow-500 dark:bg-yellow-500/20';
    default:
      return 'text-muted-foreground bg-muted dark:text-muted-foreground dark:bg-muted';
  }
};

/**
 * Format action description from audit log
 */
const formatActionDescription = (log: AuditLogApiResponse): string => {
  const { action, resource_type, details } = log;
  const resourceName = (details?.name as string) || resource_type;
  
  switch (action) {
    case 'create':
      return `Created ${resource_type} "${resourceName}"`;
    case 'update':
      return `Updated ${resource_type} "${resourceName}"`;
    case 'delete':
      return `Deleted ${resource_type} "${resourceName}"`;
    case 'read':
      return `Viewed ${resource_type} "${resourceName}"`;
    case 'grant':
      return `Granted access to ${resource_type} "${resourceName}"`;
    case 'revoke':
      return `Revoked access from ${resource_type} "${resourceName}"`;
    default:
      return `${action} on ${resource_type}`;
  }
};

/**
 * Group audit logs by day
 */
const groupLogsByDay = (logs: AuditLogApiResponse[]) => {
  const groups: { [key: string]: AuditLogApiResponse[] } = {};

  logs.forEach((log) => {
    const date = new Date(log.created_at);
    // Use browser locale for day grouping
    const dayKey = date.toLocaleDateString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    if (!groups[dayKey]) {
      groups[dayKey] = [];
    }
    groups[dayKey].push(log);
  });

  return groups;
};

/**
 * Get user initials from user ID or details
 */
const getUserInitials = (log: AuditLogApiResponse): string => {
  // Try to get from details first
  if (log.details?.user_name) {
    const name = log.details.user_name as string;
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  }
  
  // Fallback to user ID first 2 chars or 'SY' for system
  return log.user_id ? log.user_id.substring(0, 2).toUpperCase() : 'SY';
};

/**
 * Activity Feed page component
 */
export function ActivityFeedPage() {
  const rpc = useRpc();
  const { error: showError } = useToastStore();
  const [logs, setLogs] = useState<AuditLogApiResponse[]>([]);
  const [loading, setLoading] = useState(true);

  /**
   * Fetch audit logs from API using RPC
   */
  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setLoading(true);

        // No limit - fetch all audit logs for activity feed
        const res = await rpc.api.private['audit-logs'].$get({ query: {} });

        if (res.ok) {
          const data = await res.json();
          // Response data is now properly typed as PaginatedResponse<AuditLogApiResponse>
          setLogs(data.data as AuditLogApiResponse[]);
        } else {
          throw new Error('Failed to fetch audit logs');
        }
      } catch (err) {
        console.error('Failed to fetch audit logs:', err);
        showError('Failed to load activity', 'Could not retrieve audit logs');
      } finally {
        setLoading(false);
      }
    };

    void fetchLogs();
  }, [rpc, showError]);

  const logGroups = groupLogsByDay(logs);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Activity Feed</h1>
          <p className="text-muted-foreground">
            Recent activity across all tables and users
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <FoldingCube text="Loading activity..." size="md" />
            </div>
          ) : (
            <ScrollArea className="h-[600px]">
              <div className="space-y-6">
                {Object.keys(logGroups).length > 0 ? (
                  Object.entries(logGroups).map(
                    ([day, dayLogs], groupIndex) => (
                      <div key={day}>
                        <div className="mb-4 flex items-center gap-2">
                          <Separator className="flex-1" />
                          <h3 className="text-muted-foreground text-sm font-medium">
                            {day}
                          </h3>
                          <Separator className="flex-1" />
                        </div>

                        <div className="space-y-4">
                          {dayLogs.map((log) => {
                            const Icon = getActionIcon(log.action, log.resource_type);
                            const iconColor = getActionColor(log.action);

                            return (
                              <div
                                key={log.id}
                                className="flex items-start gap-2"
                              >
                                <div className={`rounded-full p-2 ${iconColor}`}>
                                  <Icon className="h-4 w-4" />
                                </div>

                                <div className="flex-1 space-y-1">
                                  <div className="flex items-center gap-2">
                                    <p className="text-sm font-medium">
                                      {formatActionDescription(log)}
                                    </p>
                                    <Badge
                                      variant="outline"
                                      className="text-xs capitalize"
                                    >
                                      {log.resource_type}
                                    </Badge>
                                  </div>
                                  
                                  {typeof log.details?.description === 'string' && log.details?.description && (
                                    <p className="text-muted-foreground text-sm">
                                      {log.details.description}
                                    </p>
                                  )}

                                  <div className="text-muted-foreground flex items-center gap-2 text-xs">
                                    <Avatar className="h-4 w-4">
                                      <AvatarFallback className="text-xs">
                                        {getUserInitials(log)}
                                      </AvatarFallback>
                                    </Avatar>
                                    <span>
                                      {new Date(log.created_at).toLocaleTimeString()}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {groupIndex < Object.keys(logGroups).length - 1 && (
                          <Separator className="mt-6" />
                        )}
                      </div>
                    )
                  )
                ) : (
                  <div className="py-8 text-center">
                    <p className="text-muted-foreground">No recent activity</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

